// src/components/PlannerProductsList.js
import React, { useState, useRef, useEffect, useCallback } from "react"
import gsap from "gsap"
import PlannerProductCard from "./PlannerProductCard"
import PlannerProductExpanded from "./PlannerProductExpanded"

/**
 * Products list for planner - 3-column grid
 * Expanded panel opens below the row of the active card with a tab pointer
 * Smooth open/close animations, auto-scroll into view
 */
const PlannerProductsList = ({
  productNodes,
  selectProductNodeInstance,
  removeProductNodeInstance,
  duplicateProductNode,
  rotateProductNodeInstance,
  changeProductNodeInstanceHeight,
  applyMaterial,
  selectedProductLocalId,
  onSelectProduct,
  language = "it",
}) => {
  const [internalSelectedId, setInternalSelectedId] = useState(null)
  // displayedId is the id currently rendered (stays during close animation)
  const [displayedId, setDisplayedId] = useState(null)
  const containerRef = useRef(null)
  const expandedRef = useRef(null)
  const isAnimatingRef = useRef(false)
  const prevProductCountRef = useRef(0)

  const activeId =
    selectedProductLocalId !== undefined
      ? selectedProductLocalId
      : internalSelectedId

  const products = Object.values(productNodes)

  // Set the actual selection state
  const setSelection = useCallback(
    (newId) => {
      if (onSelectProduct) {
        onSelectProduct(newId)
      } else {
        setInternalSelectedId(newId)
        if (newId && selectProductNodeInstance) {
          selectProductNodeInstance(productNodes[newId])()
        }
      }
    },
    [onSelectProduct, selectProductNodeInstance, productNodes],
  )

  // Animate close, then update state
  const animateClose = useCallback((onComplete) => {
    if (expandedRef.current && !isAnimatingRef.current) {
      isAnimatingRef.current = true
      gsap.to(expandedRef.current, {
        height: 0,
        opacity: 0,
        duration: 0.3,
        ease: "power2.inOut",
        onComplete: () => {
          isAnimatingRef.current = false
          onComplete()
        },
      })
    } else {
      onComplete()
    }
  }, [])

  const handleEdit = useCallback(
    (localId) => {
      if (isAnimatingRef.current) return
      const newId = activeId === localId ? null : localId

      if (displayedId) {
        // Close current panel first, then open new one (or stay closed)
        animateClose(() => {
          setDisplayedId(null)
          // Use rAF to ensure DOM has cleared before setting new
          requestAnimationFrame(() => {
            setSelection(newId)
          })
        })
      } else {
        setSelection(newId)
      }
    },
    [activeId, displayedId, animateClose, setSelection],
  )

  const handleDeselect = useCallback(() => {
    if (isAnimatingRef.current) return
    animateClose(() => {
      setDisplayedId(null)
      requestAnimationFrame(() => {
        setSelection(null)
      })
    })
  }, [animateClose, setSelection])

  // When activeId changes, sync displayedId and animate open
  useEffect(() => {
    if (activeId && activeId !== displayedId) {
      setDisplayedId(activeId)
    }
  }, [activeId])

  // Animate open when displayedId is set and ref is available
  useEffect(() => {
    if (displayedId && expandedRef.current) {
      isAnimatingRef.current = true
      gsap.fromTo(
        expandedRef.current,
        { height: 0, opacity: 0 },
        {
          height: "auto",
          opacity: 1,
          duration: 0.35,
          ease: "power2.out",
          onComplete: () => {
            isAnimatingRef.current = false
            // Scroll expanded panel into view
            expandedRef.current?.scrollIntoView({
              behavior: "smooth",
              block: "nearest",
            })
          },
        },
      )
    }
  }, [displayedId])

  // Animate new products entering (when count increases)
  useEffect(() => {
    if (!containerRef.current || products.length === 0) return

    if (products.length > prevProductCountRef.current) {
      const newItems = containerRef.current.querySelectorAll(
        "[data-flip-id]:not([data-animated])",
      )
      if (newItems.length > 0) {
        gsap.fromTo(
          newItems,
          { opacity: 0, y: 20, scale: 0.9 },
          {
            opacity: 1,
            y: 0,
            scale: 1,
            duration: 0.4,
            stagger: 0.05,
            ease: "power2.out",
            onComplete: () => {
              newItems.forEach((item) =>
                item.setAttribute("data-animated", "true"),
              )
            },
          },
        )
      }
    }
    prevProductCountRef.current = products.length
  }, [products.length])

  if (products.length === 0) {
    return (
      <div className="ay-text-center ay-text-gray-400 ay-py-12 ay-text-sm">
        <p>Nessun prodotto aggiunto</p>
        <p className="ay-text-xs ay-mt-1">No products added yet</p>
      </div>
    )
  }

  // Find column position (0,1,2) of the displayed product
  const displayedIndex = displayedId
    ? products.findIndex((p) => p.localId === displayedId)
    : -1
  const displayedCol = displayedIndex >= 0 ? displayedIndex % 3 : -1

  // Build rows of 3
  const renderItems = []
  let currentRow = []

  products.forEach((pn, index) => {
    currentRow.push(pn)

    const isEndOfRow = (index + 1) % 3 === 0 || index === products.length - 1

    if (isEndOfRow) {
      const rowStartIndex = index - (currentRow.length - 1)

      renderItems.push(
        <div key={`row-${index}`} className="ay-grid ay-grid-cols-3 ay-gap-2">
          {currentRow.map((p) => (
            <div key={p.localId} data-flip-id={`product-${p.localId}`}>
              <PlannerProductCard
                pn={p}
                onEdit={() => handleEdit(p.localId)}
                removeProductNodeInstance={removeProductNodeInstance}
                duplicateProductNode={duplicateProductNode}
                language={language}
                isActive={displayedId === p.localId}
              />
            </div>
          ))}
        </div>,
      )

      // If the displayed product is in this row, render expanded panel
      const activeInRow = currentRow.find((p) => p.localId === displayedId)
      if (activeInRow) {
        // Column position within this row
        const colInRow = displayedIndex - rowStartIndex
        // Pointer position: center of that column (each col is ~33.33%)
        const pointerLeft = `calc(${colInRow * 33.333 + 16.666}% - 8px)`

        renderItems.push(
          <div
            key={`expanded-${activeInRow.localId}`}
            ref={expandedRef}
            className="ay-overflow-hidden ay-relative"
          >
            {/* Tab pointer triangle */}
            <div
              className="ay-absolute ay-top-0 ay-z-10"
              style={{ left: pointerLeft }}
            >
              <div
                className="ay-w-0 ay-h-0"
                style={{
                  borderLeft: "8px solid transparent",
                  borderRight: "8px solid transparent",
                  borderBottom: "8px solid rgb(243 244 246)",
                }}
              />
            </div>
            <div className="ay-mt-2">
              <PlannerProductExpanded
                pn={activeInRow}
                rotateProductNodeInstance={rotateProductNodeInstance}
                changeProductNodeInstanceHeight={
                  changeProductNodeInstanceHeight
                }
                applyMaterial={applyMaterial}
                removeProductNodeInstance={removeProductNodeInstance}
                duplicateProductNode={duplicateProductNode}
                selectProductNodeInstance={selectProductNodeInstance}
                onClose={handleDeselect}
                language={language}
              />
            </div>
          </div>,
        )
      }

      currentRow = []
    }
  })

  return (
    <div ref={containerRef} className="ay-flex ay-flex-col ay-gap-2 ay-px-0.5">
      {renderItems}
    </div>
  )
}

export default PlannerProductsList
